import { BeforeCapture } from '@hello-pangea/dnd';
import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { DROP_OFFSET_ATTR, getDraggedDom } from './useDragPlaceholder';

type UseParentResizeDndProps = {
  parentPrefix: string;
};

const useParentResizeDnd = ({ parentPrefix }: UseParentResizeDndProps) => {
  const [isDraggingParent, setIsDraggingParent] = useState(false);
  const draggedElRef = useRef<HTMLElement | null>(null);

  const onBeforeCapture = useCallback(
    (before: BeforeCapture) => {
      if (!before.draggableId.startsWith(parentPrefix + '-')) return;
      if (before.mode === 'SNAP') return;

      const draggedEl = getDraggedDom(before.draggableId);
      if (!draggedEl) return;

      const beforeTop = draggedEl.getBoundingClientRect().top;

      flushSync(() => setIsDraggingParent(true));

      const afterTop = draggedEl.getBoundingClientRect().top;
      const offset = beforeTop - afterTop;

      draggedElRef.current = draggedEl;
      draggedEl.setAttribute(DROP_OFFSET_ATTR, String(offset));

      if (offset !== 0) {
        draggedEl.style.position = 'relative';
        draggedEl.style.top = `${offset}px`;
      }
    },
    [parentPrefix],
  );

  const resetDragStyles = useCallback(() => {
    setIsDraggingParent(false);
    if (draggedElRef.current) {
      draggedElRef.current.style.position = '';
      draggedElRef.current.style.top = '';
      draggedElRef.current.removeAttribute(DROP_OFFSET_ATTR);
      draggedElRef.current = null;
    }
  }, []);

  return {
    isDraggingParent,
    onBeforeCapture,
    resetDragStyles,
  };
};

export default useParentResizeDnd;
